import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { get } from 'lodash'
import { compose, pure } from 'recompose'
import {
  ParticipantOverview,
  LevelSummary,
  LabelsOverview,
  ParticipantOther
} from 'components/sections/participants'
import { Form } from 'components/layout'
import { PersonalBlock, AddressBlock } from 'components/sections/common'
import { FormButton, FloatingBar, ButtonGroup, Button } from 'components/misc'
import { form } from 'helpers/decorators'
import { apiConfiguration, apiLabels } from 'helpers/decorators/api'
import { DetailLoader } from 'components/loaders'
import { modal, updateActiveTab } from 'redux/modules/app'
import { getParticipantSummary } from 'redux/modules/api/participants'
import { validate, mapSchema } from 'models/participants'
import activeAward from 'helpers/activeAward'
import { CONFIGURATION_KEYS } from 'constants/enums'
import { asyncValidate } from 'models/persons/asyncValidate'
import { checkEmail } from '../../../../redux/modules/api/asyncValidations'

const enhance = compose(
  connect(
    ({ api, auth }) => ({
      awards: api.awards,
      persons: api.persons,
      labels: api.labels,
      availableLabels: api.labels.available.list,
      personLabels: api.labels.person.list,
      activeOrganization: auth.organization
    }),
    { modal, updateActiveTab, getParticipantSummary, checkEmail }
  ),
  apiConfiguration({
    getValues: [
      CONFIGURATION_KEYS.enableLabels.id,
      CONFIGURATION_KEYS.participantRegistrationEmailAddress2.id,
      CONFIGURATION_KEYS.participantRegistrationPhone.id,
      CONFIGURATION_KEYS.participantRegistrationPhoneType.id,
      CONFIGURATION_KEYS.participantRegistrationMiddleName.id,
      CONFIGURATION_KEYS.participantRegistrationNationality.id,
      CONFIGURATION_KEYS.participantRegistrationEmploymentStatus.id,
      CONFIGURATION_KEYS.participantRegistrationAddressLine1.id,
      CONFIGURATION_KEYS.participantRegistrationAddressLine2.id,
      CONFIGURATION_KEYS.participantRegistrationPhone.id,
      CONFIGURATION_KEYS.participantRegistrationAddressCity.id,
      CONFIGURATION_KEYS.participantRegistrationAddressZipCode.id,
      CONFIGURATION_KEYS.participantRegistrationAddressRegion.id,
      CONFIGURATION_KEYS.participantRegistrationAddressCountry.id,
      CONFIGURATION_KEYS.postalAddress.id,
      CONFIGURATION_KEYS.enableLabels.id,
      CONFIGURATION_KEYS.manageAssessors.id
    ]
  }),
  apiLabels(),
  form({
    form: 'participantUpdate',
    initialProps: 'participants.detail',
    initialPropsMapper: mapSchema,
    validate,
    asyncValidate
  }),
  pure
)

class Detail extends Component {
  static propTypes = {
    values: PropTypes.object,
    pristine: PropTypes.bool.isRequired,
    submitSucceeded: PropTypes.bool.isRequired,
    submitFailed: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    participants: PropTypes.object.isRequired,
    awards: PropTypes.object.isRequired,
    persons: PropTypes.object.isRequired,
    labels: PropTypes.object.isRequired,
    activeOrganization: PropTypes.object.isRequired,
    availableLabels: PropTypes.array.isRequired,
    personLabels: PropTypes.array.isRequired,
    codeLists: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    change: PropTypes.func,
    updateParticipant: PropTypes.func.isRequired,
    deleteParticipant: PropTypes.func.isRequired,
    sendEmailInvitation: PropTypes.func.isRequired,
    sendRecoveryPassword: PropTypes.func.isRequired,
    sendConfirmationEmail: PropTypes.func.isRequired,
    inviteParticipantToNextLevel: PropTypes.func.isRequired,
    onParticipantStateChange: PropTypes.func.isRequired,
    fetchAllowedOfflineAwardLevels: PropTypes.func.isRequired,
    onGetConfigurationValues: PropTypes.func.isRequired,
    modal: PropTypes.func.isRequired,
    updateActiveTab: PropTypes.func.isRequired,
    configuration: PropTypes.object.isRequired,
    getParticipantSummary: PropTypes.func.isRequired,
    onFetchAvailableLabels: PropTypes.func.isRequired,
    onAssignPersonLabel: PropTypes.func.isRequired,
    onUnassignPersonLabel: PropTypes.func.isRequired,
    onGetPersonLabels: PropTypes.func.isRequired,
    isOrgConfigurationTrue: PropTypes.func.isRequired
  }

  componentDidMount = async() => {
    const { participants, activeOrganization } = this.props

    const activeIndex = activeAward(participants.detail)

    const activeAwardId = get(participants, `detail.awards[${activeIndex}].id`)

    this.props.updateActiveTab('participantLevels', null)

    this.handleFetchOnLoad(activeAwardId)

    const organizationId = get(activeOrganization, 'id')

    const participantId = get(this.props.participants, 'detail.id')

    participantId && organizationId && this.props.onFetchAvailableLabels(participantId, organizationId)
    participantId && this.props.onGetPersonLabels(participantId)
  }

  componentWillReceiveProps = async(nextProps) => {
    const { participants, activeOrganization } = this.props

    if (
      get(participants, 'detail.id') !==
      get(nextProps.participants, 'detail.id')
    ) {
      const activeIndex = activeAward(nextProps.participants.detail)

      const activeAwardId = get(
        nextProps.participants,
        `detail.awards[${activeIndex}].id`
      )

      const organizationId = get(activeOrganization, 'id')

      this.handleFetchOnLoad(activeAwardId)

      const participantId = get(nextProps.participants, 'detail.id')

      participantId && organizationId && this.props.onFetchAvailableLabels(participantId, organizationId)
      participantId && this.props.onGetPersonLabels(participantId)
    }
  }

  handleFetchOnLoad = awardId => {
    if (!awardId) {
      return
    }
    this.props.fetchAllowedOfflineAwardLevels(awardId)
    this.props.getParticipantSummary(this.props.params.id)
  }

  render() {
    const {
      participants,
      awards,
      persons,
      codeLists,
      change,
      deleteParticipant,
      sendRecoveryPassword,
      inviteParticipantToNextLevel,
      sendConfirmationEmail,
      sendEmailInvitation,
      pristine,
      submitSucceeded,
      submitFailed,
      invalid,
      submitting,
      values,
      configuration,
      handleSubmit,
      updateParticipant,
      availableLabels,
      onAssignPersonLabel,
      onUnassignPersonLabel,
      onFetchAvailableLabels,
      personLabels,
      onGetPersonLabels,
      activeOrganization,
      labels
    } = this.props

    const summary = get(participants, 'detailSummary')

    const activeIndex = activeAward(values)

    const organizationId = get(activeOrganization, 'id')

    const postalAddressEnabled = get(
      configuration,
      `values[${CONFIGURATION_KEYS.postalAddress.id}].booleanValue`
    )
    const withPostalAddress = get(values, 'helpers.withPostalAddress')

    const isLabelsEnabled = get(configuration, `valuesByOrganizationId[${organizationId}][${CONFIGURATION_KEYS.enableLabels.id}].booleanValue`)

    get(
      values,
      `awards[${activeIndex}].organization.organizationHierarchyLevel.id`
    )

    const loading = participants.loading || awards.loading || persons.loading

    const showSecondaryEmail = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationEmailAddress2.id)

    const showPhone = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationPhone.id)

    const showPhoneType = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationPhoneType.id)

    const showMiddleName = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationMiddleName.id)

    const showNationality = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationNationality.id)

    const showEmploymentStatus = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationEmploymentStatus.id)

    const showAddressLine1 = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationAddressLine1.id)

    const showAddressLine2 = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationAddressLine2.id)

    const showCity = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationAddressCity.id)

    const showZipCode = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationAddressZipCode.id)

    const showRegion = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationAddressRegion.id)

    const showCountry = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.participantRegistrationAddressCountry.id)

    const showAddressBlock = showAddressLine1 || showAddressLine2 || showCity || showZipCode || showRegion || showCountry


    return (
      <div>
        <Form
          className="form"
          onSubmit={handleSubmit(formValues => {
            formValues.helpers = null
            updateParticipant(formValues)
          })}
          encType="multipart/form-data"
          loading={loading}
          contentLoader={<DetailLoader modulesCount={2} withHeavyForm />}
          isPerson
        >
          <div className="row">
            <div className="col-xs-6">
              <PersonalBlock
                titles={codeLists.titles.list}
                phoneTypes={codeLists.phoneTypes.list}
                change={change}
                data={participants.detail}
                loading={participants.loading || persons.loading}
                personState={get(participants, 'detail.personState')}
                migratedEmail={get(
                  participants,
                  'detail.participantDetail.migratedContactEmail'
                )}
                decoPersonUpdate="participantDetail"
                canChangeEmail
                disableEmail
                personId={get(participants, 'detail.id')}
                showSecondaryEmail={showSecondaryEmail}
                showTertiaryEmail={showSecondaryEmail}
                showMiddleName={showMiddleName}
                showPhone={showPhone}
                showPhoneType={showPhoneType}
              />
            </div>

            <div className="col-xs-6">
              <ParticipantOverview
                data={participants.detail}
                personStates={codeLists.personStates.list}
                userRegistrationAssessmentStates={
                  codeLists.userRegistrationAssessmentStates.list
                }
                change={change}
                deleteParticipant={deleteParticipant}
                sendEmailInvitation={sendEmailInvitation}
                sendRecoveryPassword={sendRecoveryPassword}
                sendConfirmationEmail={sendConfirmationEmail}
                inviteParticipantToNextLevel={inviteParticipantToNextLevel}
                inviteLoading={awards.inviteLoading}
                id={participants.detail.id}
                loading={participants.loading}
                modal={this.props.modal}
                onParticipantStateChange={this.props.onParticipantStateChange}
                participantDeletionTypes={
                  codeLists.participantReasonsOfDeletionTypes.list
                }
              />

              {summary && <LevelSummary
                data={summary}
                participantId={get(participants, 'detail.id')}
              />}

              {isLabelsEnabled && <LabelsOverview
                data={personLabels}
                labels={labels}
                availableLabels={availableLabels}
                onFetchAvailableLabels={onFetchAvailableLabels}
                onAssignPersonLabel={onAssignPersonLabel}
                onUnassignPersonLabel={onUnassignPersonLabel}
                onGetPersonLabels={onGetPersonLabels}
                participantId={get(participants, 'detail.id')}
                organizationId={organizationId}
              />}

            </div>
          </div>

          {showAddressBlock && <div className="row">
            <div className="col-xs-6">
              <AddressBlock
                style={{ marginBottom: 0 }}
                change={this.props.change}
                showAddressLine1={showAddressLine1}
                showAddressLine2={showAddressLine2}
                showCity={showCity}
                showZipCode={showZipCode}
                showRegion={showRegion}
                showCountry={showCountry}
              />
            </div>
          </div>}

          {postalAddressEnabled && (
            <div>
              <div className="row">
                {!withPostalAddress && (
                  <div className="col-xs-6">
                    <Button
                      type="button"
                      style={{ marginBottom: '24px' }}
                      className="btn secondary"
                      onClick={() =>
                        this.props.change('helpers.withPostalAddress', true)
                      }
                    >
                      Add Postal Address
                    </Button>
                  </div>
                )}
              </div>

              {withPostalAddress && (
                <div>
                  <div className="row">
                    <div className="col-xs-6">
                      <AddressBlock
                        title="Postal Address"
                        intlKey="moduleTitle.address"
                        formPrefix="postalAddress"
                        style={{ marginBottom: 0 }}
                        change={this.props.change}
                        showAddressLine1
                        showAddressLine2
                        showCity
                        showZipCode
                        showRegion
                        showCountry
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-6">
                      <Button
                        type="button"
                        style={{ marginBottom: '36px' }}
                        className="btn secondary"
                        onClick={() => {
                          this.props.change('helpers.withPostalAddress', false)
                          this.props.change('postalAddress', null)
                        }}
                      >
                        Remove Postal Address
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="row">
            <div className="col-xs-6">
              <ParticipantOther
                genders={codeLists.genders.list}
                nationalities={codeLists.nationalities.list}
                employmentStatuses={codeLists.employmentStatuses.list}
                levels={awards.offlineAllowedLevels}
                data={participants.detail}
                showNationality={showNationality}
                showEmploymentStatus={showEmploymentStatus}
              />
            </div>
          </div>

          <FloatingBar>
            <ButtonGroup right>
              <FormButton
                pristine={pristine}
                submitSucceeded={submitSucceeded}
                submitFailed={submitFailed}
                invalid={invalid}
                type="submit"
                intlKey="button.save"
                isLoading={submitting}
              >
                Save
              </FormButton>
            </ButtonGroup>
          </FloatingBar>
        </Form>
      </div>
    )
  }
}

export default enhance(Detail)
